import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiUser, FiCalendar, FiClock, FiHash, FiArrowRight } = FiIcons;

function OrderForm({ selectedPrison, visitingDetails, setVisitingDetails }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    prisonerName: '',
    prisonerNumber: '',
    visitDate: '',
    visitTime: '',
    visitorName: '',
    poVisitingNumber: '',
    ...visitingDetails
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setVisitingDetails(formData);
    navigate('/customer/menu');
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  if (!selectedPrison) {
    navigate('/customer');
    return null;
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-government-800 rounded-xl shadow-xl p-8 transition-colors duration-300"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-government-900 dark:text-white mb-2">
            Visit Details
          </h1>
          <p className="text-government-600 dark:text-government-300">
            Visiting {selectedPrison.name}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-government-700 dark:text-government-300 mb-2">
                Your Name
              </label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-government-400 dark:text-government-500" />
                <input
                  type="text"
                  name="visitorName"
                  value={formData.visitorName}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-government-300 dark:border-government-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-700 text-government-900 dark:text-white transition-colors duration-300"
                  placeholder="Enter your full name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-government-700 dark:text-government-300 mb-2">
                PO Visiting Number
              </label>
              <div className="relative">
                <SafeIcon icon={FiHash} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-government-400 dark:text-government-500" />
                <input
                  type="text"
                  name="poVisitingNumber"
                  value={formData.poVisitingNumber}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-government-300 dark:border-government-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-700 text-government-900 dark:text-white transition-colors duration-300"
                  placeholder="e.g., PO123456"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-government-700 dark:text-government-300 mb-2">
                Prisoner Name
              </label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-government-400 dark:text-government-500" />
                <input
                  type="text"
                  name="prisonerName"
                  value={formData.prisonerName}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-government-300 dark:border-government-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-700 text-government-900 dark:text-white transition-colors duration-300"
                  placeholder="Prisoner's full name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-government-700 dark:text-government-300 mb-2">
                Prisoner Number
              </label>
              <div className="relative">
                <SafeIcon icon={FiHash} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-government-400 dark:text-government-500" />
                <input
                  type="text"
                  name="prisonerNumber"
                  value={formData.prisonerNumber}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-government-300 dark:border-government-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-700 text-government-900 dark:text-white transition-colors duration-300"
                  placeholder="e.g., A1234BC"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-government-700 dark:text-government-300 mb-2">
                Visit Date
              </label>
              <div className="relative">
                <SafeIcon icon={FiCalendar} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-government-400 dark:text-government-500" />
                <input
                  type="date"
                  name="visitDate"
                  value={formData.visitDate}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full pl-10 pr-4 py-3 border border-government-300 dark:border-government-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-700 text-government-900 dark:text-white transition-colors duration-300"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-government-700 dark:text-government-300 mb-2">
                Visit Time
              </label>
              <div className="relative">
                <SafeIcon icon={FiClock} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-government-400 dark:text-government-500" />
                <select
                  name="visitTime"
                  value={formData.visitTime}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-government-300 dark:border-government-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-government-700 text-government-900 dark:text-white transition-colors duration-300"
                >
                  <option value="">Select time</option>
                  <option value="09:00">09:00 AM</option>
                  <option value="10:00">10:00 AM</option>
                  <option value="11:00">11:00 AM</option>
                  <option value="14:00">02:00 PM</option>
                  <option value="15:00">03:00 PM</option>
                  <option value="16:00">04:00 PM</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-government-50 dark:bg-government-700 p-4 rounded-lg transition-colors duration-300">
            <p className="text-sm text-government-600 dark:text-government-300">
              <strong>Important:</strong> Please ensure all details are correct. 
              Your PO Visiting Number will be verified upon arrival.
            </p>
          </div>

          <button
            type="submit"
            className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <span>Continue to Menu</span>
            <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
          </button>
        </form>
      </motion.div>
    </div>
  );
}

export default OrderForm;